<template>
  <button
    class="mlui-button buttons--button-01"
    :disabled="modeDisabled"
    :class="[
      'tw-flex tw-justify-center tw-items-center tw-min-w-0 gap-1 tw-h-10',
      'tw-rounded',
      `mlui-button--${buttonType}`,
      paddingClass,
      {'tw-cursor-not-allowed': modeDisabled},
      {'loading-spinner': loading},
    ]"
    :data-test="dataTest"
    type="button"
    @click.stop="clickButton">
    <div
      v-if="loading"
      class="tw-flex tw-absolute">
      <CoreSpinner
        :size="SpinnerSize.SMALL"
        :colorCssVar="spinnerColor" />
    </div>
    <SVGIcon
      v-if="iconName"
      :class="[
        {'tw-opacity-20': loading}
      ]"
      :colorClass="iconColor"
      :iconName="iconName"
      :remSize="1.25" />
    <span
      v-if="label"
      :class="[
        'label tw-truncate',
        {'tw-opacity-20': loading}
      ]">
      {{ label }}
    </span>
  </button>
</template>

<script setup lang="ts">
import CoreSpinner from 'frontcore/views/loaders/spinner/CoreSpinner.vue'
import SVGIcon from 'frontcore/views/icons/SVGIcon.vue'
import { computed } from 'vue'
import { SpinnerSize } from 'frontcore/views/loaders/spinner/types'

const props = defineProps({
  buttonType: {
    type: String,
    default: 'primary',
    validator: (value: string) => {
      const availableTypes = [
        'primary',
        'secondary',
        'ghost',
        'destructive-primary',
        'destructive-ghost',
      ]
      return availableTypes.includes(value)
    },
  },
  dataTest: String,
  disabled: Boolean,
  iconName: String,
  label: {
    type: String,
    default: '',
  },
  loading: {
    type: Boolean,
    default: false,
  },
})

const modeDisabled = computed(() => {
  return props.disabled || props.loading
})

const iconColor = computed(() => {
  switch (props.buttonType) {
  case 'primary':
  case 'destructive-primary':
    return 'tw-fill-core-neutral-cold-100'
  case 'destructive-ghost':
    return props.disabled && !props.loading ? 'tw-fill-core-neutral-cold-60' : 'tw-fill-auxiliary-error-30'
  default:
    return props.disabled && !props.loading ? 'tw-fill-core-neutral-cold-50' : 'tw-fill-core-primary-30'
  }
})

const paddingClass = computed(() => {
  if (props.label && props.iconName) {
    return 'mlui-button--icon-and-label'
  } else if (props.label && !props.iconName) {
    return 'mlui-button--only-label'
  } else if (!props.label && props.iconName) {
    return 'mlui-button--only-icon'
  }
  return ''
})

const spinnerColor = computed(() => {
  switch (props.buttonType) {
  case 'destructive-primary':
  case 'destructive-ghost':
    return 'auxiliary-error-30'
  default:
    return 'core-primary-20'
  }
})

const emit = defineEmits<(e: 'click') => void>()

const clickButton = () => {
  if (!modeDisabled.value) {
    emit('click')
  }
}

</script>

<style scoped>

.mlui-button:focus-visible {
  box-shadow:
    0 0 0 3px var(--auxiliary-info-70),
    0 0 4px var(--auxiliary-info-70);
}

.mlui-button--only-icon {
  padding: 0.5625rem 0.625rem 0.6875rem;
}

.mlui-button--only-label {
  padding: 0.375rem 1rem 0.5rem;
}

.mlui-button--icon-and-label {
  padding: 0.375rem 1rem 0.5rem 0.75rem;
}

.mlui-button--primary {
  background-color: var(--core-primary-30);
}
.mlui-button--primary:hover:not(:disabled) {
  background-color: var(--core-primary-40);
}
.mlui-button--primary:active:not(:disabled) {
  background-color: var(--core-primary-20);
}
.mlui-button--primary.loading-spinner:disabled{
  background-color: var(--core-primary-80);
  color: var(--core-primary-100);
}
.mlui-button--destructive-primary {
  background-color: var(--auxiliary-error-40);
}
.mlui-button--destructive-primary:hover:not(:disabled) {
  background-color: var(--auxiliary-error-50);
}
.mlui-button--destructive-primary:active:not(:disabled) {
  background-color: var(--auxiliary-error-20);
}
.mlui-button--destructive-primary.loading-spinner:disabled {
  background-color: var(--auxiliary-error-80);
  color: var(--core-primary-100);
}
.mlui-button--secondary, .mlui-button--ghost {
  color: var(--core-primary-30);
}
.mlui-button--secondary:hover:not(:disabled), .mlui-button--ghost:hover:not(:disabled) {
  background-color: var(--core-primary-95);
}
.mlui-button--secondary:active:not(:disabled), .mlui-button--ghost:active:not(:disabled) {
  background-color: var(--core-primary-90);
}
.mlui-button--secondary.loading-spinner:disabled, .mlui-button--ghost.loading-spinner:disabled {
  background-color: var(--core-neutral-cold-95);
  color: var(--core-primary-30);
}
.mlui-button--destructive-ghost {
  color: var(--auxiliary-error-30);
}
.mlui-button--destructive-ghost:hover:not(:disabled) {
  background-color: var(--auxiliary-error-95);
}
.mlui-button--destructive-ghost:active:not(:disabled) {
  background-color: var(--auxiliary-error-90);
}
.mlui-button--destructive-ghost.loading-spinner:disabled {
  background-color: var(--auxiliary-error-95);
  color: var(--auxiliary-error-30);
}
.mlui-button--primary, .mlui-button--destructive-primary {
  color: var(--core-primary-100);
  border: 1px solid transparent;
}
.mlui-button--primary:focus-visible, .mlui-button--destructive-primary:focus-visible {
  border-color: white;
}
.mlui-button--primary:disabled, .mlui-button--destructive-primary:disabled {
  background-color: var(--core-neutral-cold-60);
}
.mlui-button--secondary, .mlui-button--ghost, .mlui-button--destructive-ghost {
  border: 1px solid transparent;
}
.mlui-button--secondary:disabled, .mlui-button--ghost:disabled, .mlui-button--destructive-ghost:disabled {
  color: var(--core-neutral-cold-50);
}
.mlui-button--secondary {
  border: 1px solid var(--core-primary-50);
}
.mlui-button--secondary:focus-visible {
  border: 1px solid transparent;
}
.mlui-button--secondary:disabled {
  border-color: var(--core-neutral-cold-70);
}
.mlui-button--ghost, .mlui-button--ghost:disabled {
  border-color: transparent;
}

</style>
